<template>
  <v-container density="compact" dense="true">
    <CheckoutProgress :checkout-stage="checkoutStep" />
    <div class="d-flex flex-column flex-md-row">
      <v-card class="checkout-steps pa-2a ma-2">
        <v-expansion-panels
          v-model="checkoutStep"
          expand-icon="none"
          collapse-icon="mdi-pencil"
        >
          <v-expansion-panel
            :value="CheckoutStep.ACCOUNT"
            :disabled="stepDisabled(CheckoutStep.ACCOUNT)"
          >
            <v-expansion-panel-title>
              <h3>Login/Create Acount</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <LoginForm
                :allow-guest-checkout="authStore.isProtoMember"
                @continue-as-guest="continueAsGuest"
                @submit="accountSubmitted"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            :value="CheckoutStep.PURCHASER_INFORMATION"
            :disabled="stepDisabled(CheckoutStep.PURCHASER_INFORMATION)"
          >
            <v-expansion-panel-title>
              <h3>Purchaser Information</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-alert
                v-if="purchaserProblems.length"
                type="warning"
                variant="tonal"
                class="mb-4"
                title="Complete your information to check out"
              >
                <ul class="ml-4">
                  <li
                    v-for="problem in purchaserProblems"
                    :key="problem.message"
                  >
                    {{ problem.message }}
                  </li>
                </ul>
              </v-alert>
              <MemberProfileForm
                v-if="selfId && (!authStore.isProtoMember || purchaserEmail)"
                :key="selfId"
                :person-id="selfId"
                :registration="authStore.isProtoMember"
                :email="purchaserEmail ?? undefined"
                :disabled="!memberOrder?.ready.value"
                :min-emergency-contacts="hasOwnMembership ? 2 : 0"
                :person-type="
                  hasOwnMembership
                    ? PersonTypeKey.ADULT_MEMBER
                    : PersonTypeKey.FRIEND
                "
                tabbed
                submit-label="Continue to Agreements"
                @saved="profileSaved"
                @cancel="cancelPurchaser"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            :value="CheckoutStep.AGREEMENTS"
            :disabled="stepDisabled(CheckoutStep.AGREEMENTS)"
          >
            <v-expansion-panel-title>
              <h3>Agreements</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-alert
                v-for="problem in agreementProblems"
                :key="problem.message"
                type="warning"
                variant="tonal"
                class="mb-4"
                :text="problem.message"
              />
              <AgreementsForm
                :key="agreementOrderKey"
                @terms-agreed-updated="termsAgreedUpdated"
                :order-id="memberOrder?.order.value?.assignId"
              />
              <v-row class="pa-4 ga-4">
                <v-spacer />
                <v-btn
                  color="primary"
                  @click="continueFromAgreements"
                  :loading="advancing"
                  :disabled="!agreementsSatisfied && !termsAgreed"
                  >Continue to Gift Cards</v-btn
                >
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            :value="CheckoutStep.DISCOUNTS"
            :disabled="stepDisabled(CheckoutStep.DISCOUNTS)"
          >
            <v-expansion-panel-title>
              <h3>Discounts</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-alert
                v-for="problem in discountProblems"
                :key="problem.message"
                type="warning"
                variant="tonal"
                class="mb-4"
                :text="problem.message"
              />
              <DiscountsAndBalances />
              <v-row class="pa-4 ga-4">
                <v-spacer />
                <v-btn color="primary" @click="nextStep()"
                  >Continue to Payments</v-btn
                >
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <v-expansion-panel
            :value="CheckoutStep.PAYMENT_INFORMATION"
            :disabled="stepDisabled(CheckoutStep.PAYMENT_INFORMATION)"
          >
            <v-expansion-panel-title>
              <h3>Payment Information</h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-alert
                v-if="isNonProd"
                prominent
                elevation="2"
                color="error"
                :closable="false"
                icon="mdi-alert-circle"
                class="pa-1 ma-2 w-100"
              >
                <v-alert-title>NONPROD ENVIRONMENT</v-alert-title>
                <v-alert-text
                  ><strong>Do not use real payment information!</strong> Test
                  payment information:
                  https://docs.stripe.com/testing</v-alert-text
                >
              </v-alert>
              <div class="d-flex flex-column">
                <MemberPortalPayment
                  :next-step="nextStep"
                  :previous-step="previousStep"
                  :active="checkoutStep === CheckoutStep.PAYMENT_INFORMATION"
                >
                </MemberPortalPayment>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
      <v-card class="checkout-cart pa-2 ma-2 order-first order-md-last">
        <MemberOrderCart summary />
      </v-card>
    </div>
  </v-container>
</template>

<script lang="ts" setup>
import { PersonTypeKey } from "../../../cbi-generator/generated/dto/PersonTypeKey";
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import MemberProfileForm from "@/components/portal/MemberProfileForm.vue";
import { useMemberPersonId } from "@/composables/people/useMemberPersonId";
import { useAuthStore } from "@/stores/auth";
import MemberPortalPayment from "@/components/shop/MemberPortalPayment.vue";
import { useRouter } from "vue-router";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useCatalogDeferred } from "@/composables/shop/useCatalog";
import DiscountsAndBalances from "./DiscountsAndBalances.vue";
import { CheckoutStage } from "../../../cbi-generator/generated/dto/CheckoutStage";
enum CheckoutStep {
  INITIAL,
  ACCOUNT,
  PURCHASER_INFORMATION,
  AGREEMENTS,
  DISCOUNTS,
  PAYMENT_INFORMATION,
  CONFIRMATION,
}
function stepAsNumber(step: CheckoutStep) {
  if (typeof step === "number") return step;
  if (typeof CheckoutStep[step] === "number") return CheckoutStep[step];
  return step as number;
}
const router = useRouter();
const selfId = useMemberPersonId();
const authStore = useAuthStore();
const memberOrder = inject<MemberOrderSession | null>(
  memberOrderSessionKey,
  null,
);
const catalog = useCatalogDeferred();
const isNonProd = computed(
  () => window.location.hostname != "portal.community-boating.org",
);

// Remount the agreements step when cart contents change so requirements are re-resolved.
const agreementOrderKey = computed(
  () =>
    memberOrder?.order.value?.lineItems
      ?.map((line) => line.itemInstanceIdTargetIds.assignId)
      .join(",") ?? "",
);
// Only a membership for the purchaser themselves requires the adult member profile.
// Lines without a recipient default to the purchaser on the backend.
const hasOwnMembership = computed(
  () =>
    !!memberOrder?.order.value?.lineItems?.some(
      (line) =>
        (line.buyerIsPurchaser || !line.buyerName) &&
        catalog.value?.getProductFromItemInstance(
          line.itemInstanceIdTargetIds.assignId,
        )?.itemType === ItemType.MEMBERSHIP,
    ),
);

function nextAvailableStep(currentStage: CheckoutStep = 1) {
  for (var step = currentStage + 1; step <= CheckoutStep.CONFIRMATION; step++) {
    if (!stepDisabled(step)) return step;
  }
  return currentStage;
}
// Membership purchases still require the form's emergency contacts, so the purchaser must save it.
const purchaserSaved = ref(false);
const guestDecisionEmail = ref<string | null>(null);
// Accounts just created from a guest session still finish registration here, using their account email.
const purchaserEmail = computed(
  () =>
    guestDecisionEmail.value ??
    (authStore.isProtoMember
      ? authStore.accessToken?.claims.email || null
      : null),
);
// Agreed locally; the backend readiness confirms it after the next refresh.
const termsAgreed = ref(false);
const advancing = ref(false);
const purchaserSatisfied = computed(
  () =>
    !!memberOrder?.ready.value &&
    memberOrder.purchaserComplete.value &&
    (!hasOwnMembership.value || purchaserSaved.value),
);
const agreementsSatisfied = computed(
  () => !!memberOrder?.ready.value && memberOrder.isAgreed.value,
);
const purchaserProblems = computed(() =>
  authStore.isProtoMember
    ? []
    : (memberOrder?.problems(CheckoutStage.PURCHASER) ?? []),
);
const discountProblems = computed(
  () => memberOrder?.problems(CheckoutStage.DISCOUNTS) ?? [],
);
// Shown once the member tried to continue, so unanswered agreements don't warn up front.
const agreementsAttempted = ref(false);
const agreementProblems = computed(() =>
  agreementsAttempted.value && !advancing.value
    ? (memberOrder?.problems(CheckoutStage.AGREEMENTS) ?? [])
    : [],
);
const checkoutStep = ref(nextAvailableStep());
function stepDisabled(step: CheckoutStep) {
  const stepNumber = stepAsNumber(step);
  if (stepNumber == CheckoutStep.ACCOUNT) {
    return authStore.hasMemberAccount;
  }
  if (stepNumber > CheckoutStep.ACCOUNT) {
    if (authStore.isProtoMember) {
      if (
        stepNumber !== CheckoutStep.PURCHASER_INFORMATION ||
        purchaserEmail.value == null
      )
        return true;
    } else if (!authStore.isFullMemberSession) {
      return true;
    }
  }
  if (
    stepNumber > CheckoutStep.PURCHASER_INFORMATION &&
    !purchaserSatisfied.value
  )
    return true;
  if (stepNumber > CheckoutStep.AGREEMENTS && !agreementsSatisfied.value)
    return true;
  return false;
}
function stepSatisfied(step: CheckoutStep) {
  switch (stepAsNumber(step)) {
    case CheckoutStep.ACCOUNT:
      return authStore.hasMemberAccount;
    case CheckoutStep.PURCHASER_INFORMATION:
      return purchaserSatisfied.value;
    case CheckoutStep.AGREEMENTS:
      return agreementsSatisfied.value;
    default:
      return false;
  }
}
/** Advances past steps the backend already reports as complete. */
function skipSatisfiedSteps() {
  let step = stepAsNumber(checkoutStep.value);
  while (stepSatisfied(step)) {
    const next = nextAvailableStep(step);
    if (next === step) break;
    step = next;
  }
  checkoutStep.value = step;
}
function waitFor(condition: () => boolean) {
  return new Promise<void>((resolve) => {
    if (condition()) return resolve();
    const stop = watch(condition, (met) => {
      if (!met) return;
      stop();
      resolve();
    });
  });
}
/**
 * Waits for order state loaded after this call. Saves and logins renew tokens, which makes the
 * session reset and reopen, so the reopen's load stands in for an explicit refresh.
 */
async function loadFresh(): Promise<boolean> {
  const session = memberOrder;
  if (!session) return false;
  await nextTick();
  for (;;) {
    await waitFor(() => !session.busy.value);
    if (!session.orderRef.value) {
      const after = session.loads.value;
      await waitFor(() => session.loads.value > after && session.ready.value);
      return true;
    }
    try {
      await session.refresh();
      return session.ready.value;
    } catch {
      // Another operation started first; its load may predate this request, so refresh after it.
      if (session.busy.value) continue;
      // The cart reload path reports load failures.
      return false;
    }
  }
}
let advanceRequest = 0;
/** Reloads readiness, then moves past the current step if it (and later ones) are satisfied. */
async function advance() {
  const request = ++advanceRequest;
  const from = stepAsNumber(checkoutStep.value);
  advancing.value = true;
  try {
    const fresh = await loadFresh();
    if (!fresh || request !== advanceRequest) return;
    if (stepAsNumber(checkoutStep.value) === from) skipSatisfiedSteps();
  } finally {
    if (request === advanceRequest) advancing.value = false;
  }
}

function termsAgreedUpdated(agreed: boolean) {
  termsAgreed.value = agreed;
  if (agreed && !memberOrder?.isAgreed.value) void loadFresh();
}
function continueFromAgreements() {
  agreementsAttempted.value = true;
  if (agreementsSatisfied.value) nextStep();
  else void advance();
}

function accountSubmitted() {
  guestDecisionEmail.value = null;
  void advance();
}

function continueAsGuest(email: string) {
  if (!authStore.isProtoMember) return;
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return;
  guestDecisionEmail.value = normalizedEmail;
  checkoutStep.value = nextAvailableStep(CheckoutStep.ACCOUNT);
}

function profileSaved() {
  purchaserSaved.value = true;
  void advance();
}
function cancelPurchaser() {
  if (authStore.hasMemberAccount) router.push({ name: "home" });
  else checkoutStep.value = CheckoutStep.ACCOUNT;
}
watch(
  selfId,
  () => {
    purchaserSaved.value = false;
    termsAgreed.value = false;
    guestDecisionEmail.value = null;
    checkoutStep.value = authStore.hasMemberAccount
      ? CheckoutStep.PURCHASER_INFORMATION
      : CheckoutStep.ACCOUNT;
    void advance();
  },
  { immediate: true },
);
function nextStep() {
  checkoutStep.value = nextAvailableStep(checkoutStep.value);
  if (stepAsNumber(checkoutStep.value) == CheckoutStep.CONFIRMATION) {
    router.push({
      name: "order-confirmation",
      params: { id: memberOrder?.order.value?.assignId },
    });
  }
  if (checkoutStep.value > CheckoutStep.CONFIRMATION)
    checkoutStep.value = CheckoutStep.CONFIRMATION;
}
function previousStep() {
  checkoutStep.value--;
  if (stepAsNumber(checkoutStep.value) < CheckoutStep.INITIAL)
    checkoutStep.value = CheckoutStep.INITIAL;
}
</script>

<style scoped>
/* Side by side (2:1) on wide screens; stacked, cart first, on narrow ones. */
@media (min-width: 960px) {
  .checkout-steps {
    flex: 2 1 0;
    min-width: 0;
  }
  .checkout-cart {
    flex: 1 1 0;
    min-width: 0;
  }
}
</style>
